import { Box, Button, TextField, Typography } from '@mui/material';
import { useState } from 'react';

import calendarShareVideo from '../../assets/leads-vo-calendar-share.mp4';
import { useServiceAccountEmail } from '../../hooks/useLeadsVoQueries';

// Guide de connexion Google Calendar : vidéo pas-à-pas, checklist écrite, adresse
// Skipcar à partager et champ identifiant. Utilisé par l'étape 2 de l'onboarding
// et par la page dédiée /leads-vo/calendrier (envoyable aux agences qui ont
// passé l'étape).
const CalendarShareGuide = ({
  calendarId,
  setCalendarId,
  error,
}: {
  calendarId: string;
  setCalendarId: (v: string) => void;
  error: string;
}) => {
  const { data: saData } = useServiceAccountEmail();
  const [copied, setCopied] = useState(false);

  const copyEmail = async () => {
    if (saData?.email) {
      try {
        await navigator.clipboard.writeText(saData.email);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {}
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      {/* Tuto vidéo : reproduit pas à pas le partage dans l'UI Google Calendar actuelle */}
      <Box sx={{ borderRadius: 2, overflow: 'hidden', backgroundColor: '#000', position: 'relative' }}>
        <video
          src={calendarShareVideo}
          autoPlay
          loop
          muted
          playsInline
          controls
          style={{ width: '100%', display: 'block' }}
        />
      </Box>

      {/* Étapes écrites — utile pour ceux qui préfèrent suivre une checklist */}
      <Box component="ol" sx={{ pl: 2, m: 0, '& li': { mb: 1.5, fontSize: 14, color: '#262626', lineHeight: 1.5 } }}>
        <li>
          Ouvrez{' '}
          <a
            href="https://calendar.google.com/calendar/u/0/r/settings"
            target="_blank"
            rel="noreferrer"
            style={{ color: '#FDBB5F', textDecoration: 'underline' }}
          >
            Google Calendar → Paramètres
          </a>
          , puis sélectionnez votre calendrier dans la colonne de gauche.
        </li>
        <li>
          Dans <strong>« Partager avec des personnes ou des groupes spécifiques »</strong>, cliquez sur
          <strong> « Ajouter des personnes »</strong> et collez l'adresse Skipcar (ci-dessous).
        </li>
        <li>
          Sélectionnez la permission <strong>« Apporter des modifications aux événements »</strong>, puis{' '}
          <strong>« Envoyer »</strong>.
        </li>
        <li>
          Revenez ici et collez l'<strong>identifiant de votre calendrier</strong> dans le champ en bas (Paramètres →
          « Intégrer le calendrier » → « ID de l'agenda » ; pour le calendrier principal, c'est votre adresse email
          Google).
        </li>
      </Box>

      {/* Adresse Skipcar à partager (copiable) */}
      {saData?.email && (
        <Box>
          <Typography sx={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: '#464646', mb: 1 }}>
            Adresse à partager avec votre calendrier
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1.5,
              backgroundColor: '#F5F5F5',
              borderRadius: 1,
              border: '1px solid #E0E0E0',
            }}
          >
            <Typography sx={{ fontFamily: 'monospace', fontSize: 13, color: '#262626', wordBreak: 'break-all', flex: 1 }}>
              {saData.email}
            </Typography>
            <Button
              size="small"
              onClick={copyEmail}
              sx={{ textTransform: 'none', color: '#FDBB5F', fontWeight: 700, fontSize: 13, minWidth: 'auto' }}
            >
              {copied ? 'Copié !' : 'Copier'}
            </Button>
          </Box>
          <Typography sx={{ fontSize: 11, color: '#9E9E9E', mt: 1 }}>
            Permission à donner : <strong>« Apporter des modifications aux événements »</strong>.
          </Typography>
        </Box>
      )}

      {/* Champ identifiant calendrier */}
      <Box>
        <Typography sx={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: '#464646', mb: 1 }}>
          Identifiant du calendrier Google
        </Typography>
        <TextField
          value={calendarId}
          onChange={(e) => setCalendarId(e.target.value)}
          fullWidth
          placeholder="ton.email@gmail.com ou c_abc123…@group.calendar.google.com"
        />
        <Typography sx={{ fontSize: 11, color: '#9E9E9E', mt: 1 }}>
          Pour le calendrier principal, c'est généralement votre adresse email Google.
        </Typography>
      </Box>

      {error && <Typography sx={{ color: '#C62828', fontSize: 14 }}>{error}</Typography>}
    </Box>
  );
};

export default CalendarShareGuide;
